package dev.lingvo.shared.screenshots.templates

import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.screenshots.doc.BackgroundLayer
import dev.lingvo.shared.screenshots.doc.Board
import dev.lingvo.shared.screenshots.doc.Box
import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.FrameFinish
import dev.lingvo.shared.screenshots.doc.ImageLayer
import dev.lingvo.shared.screenshots.doc.LayerBase
import dev.lingvo.shared.screenshots.doc.NRect
import dev.lingvo.shared.screenshots.doc.Page
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.doc.Paint
import dev.lingvo.shared.screenshots.doc.Role
import dev.lingvo.shared.screenshots.doc.SceneKind
import dev.lingvo.shared.screenshots.doc.SceneSpec
import dev.lingvo.shared.screenshots.doc.ShapeKind
import dev.lingvo.shared.screenshots.doc.ShapeLayer
import dev.lingvo.shared.screenshots.doc.Stop
import dev.lingvo.shared.screenshots.doc.StrokeSpec
import dev.lingvo.shared.screenshots.doc.TextAlign
import dev.lingvo.shared.screenshots.doc.TextLayer
import dev.lingvo.shared.screenshots.geom.safeArea
import kotlin.math.min
import kotlin.math.round

/** Three plan-relative captures expose breadth; the existing renderer wraps neighbours at the set boundary. */
object Fan : Template by StyledTemplate("fan", "Fan", TemplateCategory.BOLD, "Plus Jakarta Sans", 700,
    .085, .042, 1.08, -.01, FrameFinish.SILVER, SceneSpec(SceneKind.FAN_OF_THREE), TextAlign.CENTER)

/** Related perceptual anchors remain editable tokens instead of hard-coded, brand-specific gradient colours. */
object Gradient : Template by DetailTemplate(StyledTemplate("gradient", "Gradient", TemplateCategory.BOLD,
    "Manrope", 700, .090, .042, 1.08, -.02, FrameFinish.SILVER))

/** The grid and two real capture crops make alignment visible without synthetic feature illustrations. */
object Grid : Template by DetailTemplate(StyledTemplate("grid", "Grid", TemplateCategory.MINIMAL,
    "Space Grotesk", 600, .085, .042, 1.10, -.01, FrameFinish.GRAPHITE, SceneSpec(SceneKind.FLOATING_CARDS)))

/** One round source crop magnifies a real feature; dark radial light separates the device from the ground. */
object Spotlight : Template by DetailTemplate(StyledTemplate("spotlight", "Spotlight", TemplateCategory.DARK,
    "Manrope", 700, .090, .042, 1.08, 0.0, FrameFinish.GRAPHITE, SceneSpec(SceneKind.FLOATING_CARDS), TextAlign.CENTER))

/** Extra artwork stays ordinary page layers so saves, snapshots and derivation need no template-specific state. */
private class DetailTemplate(private val recipe: Template) : Template by recipe {
    override fun build(brand: BrandKit, board: Board, slots: List<TemplateSlot>, palette: Palette): List<Page> {
        val pages = recipe.build(brand, board, slots, palette)
        // Fixed listing artwork deliberately has no screenshot dependency or ornamental device.
        if (board.deviceClass == DeviceClass.PLAY_FEATURE_GRAPHIC.id) return pages
        return pages.map { page -> when (id.id) {
            "gradient" -> page.copy(layers = page.layers.map { layer ->
                if (layer is BackgroundLayer) layer.copy(fill = Paint.Linear(160.0,
                    listOf(Stop(0.0, "@bg"), Stop(.5, "@surface"), Stop(1.0, "@accent")))) else layer
            })
            "grid" -> grid(page, board)
            "spotlight" -> spotlight(page, board)
            else -> page
        } }
    }

    private fun grid(page: Page, board: Board): Page {
        val w = board.canvas.width.toDouble()
        val h = board.canvas.height.toDouble()
        val safe = safeArea(board, defaultFrame(board))
        val layers = page.layers.map { layer -> when (layer) {
            is ImageLayer -> {
                val box = layer.base.box
                val regionX = if (w > h) .42 * w else 0.0
                val width = sceneWidth(board)
                layer.copy(base = layer.base.copy(box = box.copy(x = regionX + .5 * width, w = .44 * width),
                    shadow = layer.base.shadow?.copy(color = "@ink/0.18")),
                    cornerRadius = min(.025 * width, min(box.h, .44 * width) / 2),
                    stroke = StrokeSpec((.002 * width).coerceAtMost(200.0), Paint.Solid("@ink/0.12")))
            }
            is TextLayer -> if (layer.base.role == Role.CAPTION && w <= h) {
                val box = layer.base.box
                layer.copy(base = layer.base.copy(box = box.copy(w = box.w * .75)))
            } else layer
            else -> layer
        } }.toMutableList()
        val guides = (0..8).map { column ->
            ShapeLayer(LayerBase("${page.id}-col$column", "Column guide",
                Box((safe.x + column * safe.w / 8).coerceAtMost(safe.right - 2.0), 0.0, 2.0, h)),
                ShapeKind.RECT, fill = Paint.Solid("@ink/0.06"))
        }
        layers.addAll(1, guides)
        val caption = page.layers.first { it.base.role == Role.CAPTION }.base.box
        layers += ShapeLayer(LayerBase("${page.id}-bullet", "Accent bullet",
            Box((caption.x - .035 * w).coerceAtLeast(0.0), caption.y + .02 * w, .016 * w, .016 * w), role = Role.DECOR),
            ShapeKind.RECT, fill = Paint.Solid("@accent"))
        return page.copy(layers = layers)
    }

    private fun spotlight(page: Page, board: Board): Page {
        val width = sceneWidth(board)
        val layers = page.layers.filterNot { it.base.role == Role.CARD_2 }.map { layer -> when (layer) {
            is BackgroundLayer -> layer.copy(fill = Paint.Radial(.5, -.1, 1.1,
                listOf(Stop(0.0, "@surface"), Stop(1.0, "@bg"))))
            is ImageLayer -> {
                val diameter = .42 * width
                // Unbound artwork scales with its board while scene devices and captions reflow independently.
                layer.copy(base = layer.base.copy(box = layer.base.box.copy(w = diameter, h = diameter), role = null,
                    shadow = layer.base.shadow?.copy(color = "@bg/0.4")),
                    crop = NRect(.1, .28, .8, .32), cornerRadius = diameter / 2,
                    stroke = StrokeSpec((.008 * width).coerceAtMost(200.0), Paint.Solid("@accent")))
            }
            else -> layer
        } }
        return page.copy(layers = layers)
    }

    private fun sceneWidth(board: Board): Double = if (board.canvas.width > board.canvas.height)
        .58 * board.canvas.width - round(.06 * board.canvas.width) else board.canvas.width.toDouble()
}
